import { getEventScope } from "../core/event-scope/event-scope";
import { Roles } from "../constants/roles";
import { notifier } from "../core/notifier/notifier";
import { ModalHandler } from "./modal-handler";
import { ModalForm } from "./modal-form";
import { ModalRender } from "./modal-render";
import type { ModalSnsLink } from "./modal-render";

type OriginalEditData = {
    post_title?: string;
    content?: string;
    sns_links?: ModalSnsLink[];
    post_product_id?: number | null;
};

function parseOriginalEditData(raw: string): OriginalEditData | null {
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? (parsed as OriginalEditData) : null;
    } catch {
        return null;
    }
}

export class ModalListener {
    private readonly handler: ModalHandler;

    private readonly render: ModalRender;

    private readonly modalForm: ModalForm;

    public constructor(handler: ModalHandler, render: ModalRender) {
        this.handler = handler;
        this.render = render;
        this.modalForm = new ModalForm();
    }

    public isMeaningfulHtmlContent(html: string): boolean {
        try {
            const raw = String(html || "");
            const trimmed = raw.trim();
            if (!trimmed) return false;

            if (!trimmed.includes("<")) {
                return trimmed !== "";
            }

            const wrapper = document.createElement("div");
            wrapper.innerHTML = trimmed;

            if (this.modalForm.hasMeaningfulMedia(wrapper)) return true;

            const text = String(wrapper.textContent || "")
                .replace(/\u00a0/g, " ")
                .trim();
            return text !== "";
        } catch {
            return true;
        }
    }

    public isEditFormActuallyDirty(modal: Element): boolean {
        try {
            const postDetail = this.modalForm.getPostDetail(modal);
            if (!postDetail) return true;

            const originalRaw = String(postDetail.dataset.originalEditData || "");
            if (!originalRaw) return true;

            const original = parseOriginalEditData(originalRaw);
            if (!original) {
                return true;
            }

            const editForm =
                this.modalForm.getPostEditFormInner(postDetail) || this.modalForm.getPostEditFormInner(modal);
            if (!editForm) return true;

            const fileInputs = this.modalForm.getFileInputs(editForm);
            for (const el of fileInputs) {
                if (el.files && el.files.length > 0) return true;
            }

            const removedImages = this.modalForm.getRemovedAttachedImagesInput(editForm);
            const removedVideos = this.modalForm.getRemovedAttachedVideosInput(editForm);
            const removedImagesVal = String(removedImages?.value || "").trim();
            const removedVideosVal = String(removedVideos?.value || "").trim();
            if (removedImagesVal && removedImagesVal !== "[]") return true;
            if (removedVideosVal && removedVideosVal !== "[]") return true;

            const titleInput = this.modalForm.getTitleInput(editForm);
            const contentInput = this.modalForm.getContentTextarea(editForm);
            const currentPostTitle = String(titleInput?.value || "").trim();
            const currentContent = String(contentInput?.value || "");

            const originalPostTitle = String(original.post_title || "").trim();
            const originalContent = String(original.content || "");

            if (currentPostTitle !== originalPostTitle) return true;
            if (currentContent !== originalContent) return true;

            const currentLinks = this.modalForm
                .getSnsLinkInputs(editForm)
                .map((el) => el.value.trim())
                .filter(Boolean)
                .sort();
            const originalLinks = (Array.isArray(original.sns_links) ? original.sns_links : [])
                .map((v) => String(v || "").trim())
                .filter(Boolean)
                .sort();
            if (JSON.stringify(currentLinks) !== JSON.stringify(originalLinks)) return true;

            const productIdInput = this.modalForm.getProductIdInput(editForm);
            const currentPostProductId = String(productIdInput?.value ?? "").trim();
            const originalPostProductId = String(original.post_product_id ?? "").trim();
            if (currentPostProductId !== originalPostProductId) return true;

            return false;
        } catch {
            return true;
        }
    }

    public isCreateFormDirty(form: HTMLFormElement): boolean {
        // Nothing meaningful to lose if the body itself is still empty — don't
        // block outside-click close with the "use the close button" warning
        // in that case, regardless of other field state (title, attachments, etc).
        const contentEl = this.modalForm.getContentTextarea(form);
        const contentRaw = contentEl ? String(contentEl.value || "") : "";
        if (!this.isMeaningfulHtmlContent(contentRaw)) return false;

        return true;
    }

    public setup(): void {
        this.setupOpenModalListener();
        this.setupCloseListeners();
        this.setupTitleDirtyTracking();
    }

    public setupTitleDirtyTracking(): void {
        const docScope = getEventScope(document, "modal-title-dirty");
        docScope.once("custom-event", () => {
            docScope.on("modal:title:dirty", (e) => {
                const detail = e instanceof CustomEvent ? e.detail : null;
                const dirty = Boolean(detail && typeof detail === "object" && "dirty" in detail && detail.dirty);
                this.render.setTitleDirty?.(dirty);
            });

            docScope.on("app:content:inserted", (e) => {
                const detail = e instanceof CustomEvent ? e.detail : null;
                const root = detail && typeof detail === "object" && "root" in detail ? detail.root : null;
                if (!(root instanceof Element)) return;

                this.render.setTitleDirty?.(false);

                if (root.getAttribute("data-modal-dirty-tracker") === "1") return;
                root.setAttribute("data-modal-dirty-tracker", "1");

                const markDirty = (evt: Event) => {
                    const target = evt?.target;
                    if (!(target instanceof Element)) return;

                    try {
                        if (evt instanceof Event && evt.isTrusted === false) return;
                    } catch {
                        // ignore
                    }

                    const createForm = this.modalForm.getClosestPostCreateForm(target);
                    const editForm = this.modalForm.getClosestPostEditFormInner(target);
                    const inCreate = Boolean(createForm);
                    const inEdit = Boolean(editForm);
                    if (!inCreate && !inEdit) return;

                    if (inEdit) {
                        const modal = this.render.getModal?.();
                        const isEditModeActive = Boolean(this.handler?.state?.isEditModeActive?.(modal));
                        if (!isEditModeActive) return;

                        const editFormWrapper = this.modalForm.getClosestPostEditFormWrapper(editForm);
                        if (editFormWrapper instanceof HTMLElement && editFormWrapper.style.display === "none") return;
                    }

                    if (inCreate) {
                        if (createForm instanceof HTMLElement && createForm.offsetParent === null) return;
                    }

                    this.render.setTitleDirty?.(true);
                };

                root.addEventListener("input", markDirty, true);
                root.addEventListener("change", markDirty, true);
            });
        });
    }

    public setupOpenModalListener(): void {
        const scope = getEventScope(document, "modal");

        scope.once("open-click", () => {
            scope.delegate("click", `[data-role="${Roles.ELEMENT_MODAL_TRIGGER}"]`, (e, linkEl) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                if (!(linkEl instanceof HTMLElement)) return;
                this.handler.handleOpenModal(linkEl);
            });

            scope.delegate("click", `[data-role="${Roles.POST_CARD_BODY}"]`, (e, cardBodyEl) => {
                const target = e.target;
                if (!(target instanceof Element)) return;

                if (
                    target.tagName === "A" ||
                    this.modalForm.getClosestAnchor(target) ||
                    target.tagName === "BUTTON" ||
                    this.modalForm.getClosestButton(target) ||
                    target.tagName === "SUMMARY" ||
                    this.modalForm.getClosestSummary(target)
                ) {
                    return;
                }

                e.preventDefault();
                e.stopImmediatePropagation();

                if (!(cardBodyEl instanceof HTMLElement)) return;
                this.handler.handleCardBodyClick(cardBodyEl);
            });

            // Prefetch modal content on hover so click is near-instant.
            // mouseover bubbles (mouseenter does not) so delegation works here.
            // prefetchPostDetail guards against duplicate/concurrent fetches internally.
            scope.delegate(
                "mouseover",
                `[data-role="${Roles.POST_CARD_BODY}"]`,
                (_e, cardBodyEl) => {
                    if (!(cardBodyEl instanceof HTMLElement)) return;
                    const modalUsage = cardBodyEl.dataset.modalUsage;
                    if (modalUsage !== "MODAL" && modalUsage !== "2") return;
                    const rawPostId = cardBodyEl.dataset.postId;
                    const boardId = cardBodyEl.dataset.boardId;
                    if (!rawPostId || !boardId) return;
                    const postId = parseInt(rawPostId, 10);
                    if (!Number.isInteger(postId) || postId <= 0) return;
                    this.render.prefetchPostDetail(postId, boardId);
                },
            );
        });
    }

    public setupCloseListeners(): void {
        const overlay = this.render.getOverlay();
        const overlayScope = getEventScope(overlay, "modal-overlay");
        overlayScope.once("overlay-click", () => {
            const handleCloseAttempt = (e: Event) => {
                try {
                    const pointerLikeEvent = e as MouseEvent;
                    if (typeof pointerLikeEvent.button === "number" && pointerLikeEvent.button !== 0) return;
                } catch {
                    // ignore
                }

                const target = e.target;
                if (!(target instanceof Node)) return;

                const modal = this.render.getModal?.();
                if (!(modal instanceof Element)) return;

                const isInsideModal = (() => {
                    try {
                        if (typeof e.composedPath === "function") {
                            const path = e.composedPath();
                            return Array.isArray(path) && path.includes(modal);
                        }
                    } catch {
                        // ignore
                    }
                    return modal.contains(target);
                })();

                if (isInsideModal) return;

                try {
                    const isEditModeActive = Boolean(this.handler?.state?.isEditModeActive?.(modal));
                    if (isEditModeActive) {
                        const titleDirty = Boolean(this.render.isTitleDirty?.());
                        if (titleDirty) {
                            const actuallyDirty = this.isEditFormActuallyDirty(modal);
                            if (!actuallyDirty) {
                                this.render.setTitleDirty?.(false);
                            } else {
                                notifier.warn("modal_click_outside_to_close");
                                return;
                            }
                        }

                        const titleDirty2 = Boolean(this.render.isTitleDirty?.());
                        if (titleDirty2) {
                            notifier.warn("modal_click_outside_to_close");
                            return;
                        }

                        if (typeof this.handler.handleHeaderCloseRequest === "function") {
                            this.handler.handleHeaderCloseRequest();
                            return;
                        }

                        this.handler.handleCloseRequest();
                        return;
                    }
                } catch {
                    // ignore
                }

                const modalBody = this.render.getModalBody?.();
                const createForm = modalBody instanceof Element ? this.modalForm.getPostCreateForm(modalBody) : null;

                const titleDirty = Boolean(this.render.isTitleDirty?.());
                const createDirty = Boolean(createForm && this.isCreateFormDirty(createForm));
                if (titleDirty || createDirty) {
                    if (createDirty && !titleDirty) this.render.setTitleDirty?.(true);
                    notifier.warn("modal_click_outside_to_close");
                    return;
                }

                this.handler.handleCloseRequest();
            };

            if (typeof window !== "undefined" && "PointerEvent" in window) {
                overlayScope.on("pointerdown", handleCloseAttempt);
            } else {
                overlayScope.on("click", handleCloseAttempt);
            }

            overlayScope.delegate("click", `[data-role="${Roles.POST_CREATE_CANCEL}"]`, (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.render.setTitleDirty?.(false);
                this.handler.handleCloseRequest();
            });
        });

        const modal = this.render.getModal();
        const closeBtn = modal ? this.modalForm.getElementModalClose(modal) : null;
        if (closeBtn) {
            const closeBtnScope = getEventScope(closeBtn, "modal-close-btn");
            closeBtnScope.once("click", () => {
                closeBtnScope.on("click", () => {
                    if (typeof this.handler.handleHeaderCloseRequest === "function") {
                        this.handler.handleHeaderCloseRequest();
                        return;
                    }
                    this.handler.handleCloseRequest();
                });
            });
        }

        const docScope = getEventScope(document, "modal");
        docScope.once("escape", () => {
            document.addEventListener(
                "keydown",
                (e: KeyboardEvent) => {
                    if (e.key !== "Escape") return;

                    const modal = this.render.getModal?.();
                    const overlay = this.modalForm.getElementModalOverlay(document);
                    if (!(modal instanceof HTMLElement) || !(overlay instanceof HTMLElement)) {
                        return;
                    }

                    const modalVisible = window.getComputedStyle(modal).display !== "none";
                    const overlayVisible = window.getComputedStyle(overlay).display !== "none";
                    if (!modalVisible || !overlayVisible) {
                        return;
                    }

                    this.handler.handleCloseRequest();
                },
                true,
            );

            docScope.on("keydown", (e) => {
                if (e instanceof KeyboardEvent && e.key === "Escape" && this.render.isOpen()) {
                    this.handler.handleCloseRequest();
                }
            });
        });
    }
}
